import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { DirhamIconColored } from '../../components/DirhamIcon';
import CurrencyDisplay from '../../components/CurrencyDisplay';
import useElementToPdf from '../../utils/useElementToPdf';
import { 
  Calendar,
  Users,
  TrendingUp,
  TrendingDown,
  Printer,
  ArrowRight,
  Send,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Languages,
  Globe
} from 'lucide-react';

const WeeklyReport = () => {
  const { week_start } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { language } = useLanguage();
  const printRef = useRef();
  
  const [report, setReport] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Language settings for print
  const [printLanguage, setPrintLanguage] = useState(language);
  const [bilingualPrint, setBilingualPrint] = useState(false);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  
  // Translations
  const TEXTS = {
    ar: {
      weeklyReport: 'التقرير الأسبوعي',
      periodFrom: 'الفترة من',
      to: 'إلى',
      print: 'طباعة',
      sendViaWhatsApp: 'إرسال عبر الواتساب',
      sending: 'جاري الإرسال...',
      revenue: 'الإيرادات',
      expenses: 'المصروفات',
      netProfit: 'صافي الربح',
      clients: 'العملاء',
      barber: 'الحلاق',
      avgPerClient: 'المعدل/عميل',
      total: 'الإجمالي',
      barbersPerformance: 'أداء الحلاقين',
      noData: 'لا توجد بيانات',
      noReport: 'لا يوجد تقرير',
      reportEligible: 'التقرير مؤهل للإرسال التلقائي',
      reportNotEligible: 'التقرير غير مؤهل للإرسال التلقائي',
      clientsCount: 'عدد العملاء',
      minimumRequired: 'الحد الأدنى المطلوب',
      reportSentLogs: 'سجل التقارير المرسلة',
      sentTo: 'تم الإرسال إلى',
      generatedOn: 'تم إنشاء التقرير في',
      reportLanguage: 'لغة التقرير',
      selectLanguage: 'اختر اللغة',
      bilingual: 'ثنائي اللغة',
      printOptions: 'خيارات الطباعة'
    },
    en: {
      weeklyReport: 'Weekly Report',
      periodFrom: 'Period from',
      to: 'to',
      print: 'Print',
      sendViaWhatsApp: 'Send via WhatsApp',
      sending: 'Sending...',
      revenue: 'Revenue',
      expenses: 'Expenses',
      netProfit: 'Net Profit',
      clients: 'Clients',
      barber: 'Barber',
      avgPerClient: 'Avg/Client',
      total: 'Total',
      barbersPerformance: 'Barbers Performance',
      noData: 'No data',
      noReport: 'No report',
      reportEligible: 'Report eligible for auto-send',
      reportNotEligible: 'Report not eligible for auto-send',
      clientsCount: 'Clients count',
      minimumRequired: 'Minimum required',
      reportSentLogs: 'Sent Reports Log',
      sentTo: 'Sent to',
      generatedOn: 'Generated on',
      reportLanguage: 'Report Language',
      selectLanguage: 'Select Language',
      bilingual: 'Bilingual',
      printOptions: 'Print Options'
    }
  };
  
  const isArabic = language === 'ar';
  
  const getText = (key, lang = printLanguage) => {
    return TEXTS[lang]?.[key] || TEXTS['ar'][key];
  };
  
  const getBilingualText = (key) => {
    if (bilingualPrint) {
      return `${TEXTS['ar'][key]} / ${TEXTS['en'][key]}`;
    }
    return getText(key);
  };

  // react-to-print hook
  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `${getBilingualText('weeklyReport')}_${report?.week_start || 'report'}`,
    pageStyle: `
      @page { size: A4; margin: 15mm; }
      @media print {
        body { 
          -webkit-print-color-adjust: exact !important; 
          print-color-adjust: exact !important;
          direction: ${bilingualPrint ? 'rtl' : (printLanguage === 'ar' ? 'rtl' : 'ltr')};
        }
        .print\\:hidden { display: none !important; }
        .print\\:block { display: block !important; }
      }
    `
  });
  
  const triggerPrint = () => {
    setShowPrintOptions(false);
    setTimeout(() => handlePrint(), 100);
  };

  useEffect(() => {
    fetchReport();
    if (isAdmin()) {
      fetchLogs();
    }
  }, [week_start]);

  const fetchReport = async () => {
    try {
      let res;
      if (week_start) {
        res = await api.get(`/reports/weekly/${week_start}`);
      } else {
        res = await api.get('/admin/weekly-report');
      }
      setReport(res.data);
    } catch (error) {
      toast.error('فشل تحميل التقرير');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await api.get('/admin/weekly-report/logs');
      setLogs(res.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleSendReport = async () => {
    setSending(true);
    try {
      await api.post('/admin/weekly-report/send');
      toast.success('تم إرسال التقرير عبر الواتساب');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال التقرير');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{isArabic ? 'لا يوجد تقرير' : 'No report'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="weekly-report-page">
      {/* Print Options Modal */}
      {showPrintOptions && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-card p-6 rounded-xl max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-center gap-2 text-lg font-bold">
              <Globe className="w-5 h-5 text-primary" />
              {isArabic ? 'خيارات الطباعة' : 'Print Options'}
            </div>
            
            {/* Language Selection */}
            <div className="space-y-3 border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {isArabic ? 'لغة التقرير' : 'Report Language'}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setPrintLanguage('ar')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      printLanguage === 'ar' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    عربي
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintLanguage('en')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      printLanguage === 'en' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    English
                  </button>
                </div>
              </div>
              
              {/* Bilingual Toggle */}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">{isArabic ? 'ثنائي اللغة' : 'Bilingual'}</span>
                </div>
                <Switch
                  checked={bilingualPrint}
                  onCheckedChange={setBilingualPrint}
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={triggerPrint} className="flex-1">
                <Printer className="w-4 h-4 me-2" />
                {isArabic ? 'تنزيل PDF' : 'Download PDF'}
              </Button>
              <Button onClick={() => setShowPrintOptions(false)} variant="outline">
                {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header - Hide in print */}
      <div className="flex justify-between items-center print:hidden">
        <div>
          <h1 className="text-3xl font-bold">{isArabic ? 'التقرير الأسبوعي' : 'Weekly Report'}</h1>
          <p className="text-muted-foreground">
            {isArabic ? 'الفترة من' : 'Period from'} {report.week_start} {isArabic ? 'إلى' : 'to'} {report.week_end}
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowPrintOptions(true)} variant="outline" data-testid="print-btn">
            <Printer className="w-4 h-4 me-2" />
            {isArabic ? 'تنزيل PDF' : 'Download PDF'}
          </Button>
          {isAdmin() && (
            <Button 
              onClick={handleSendReport} 
              disabled={sending}
              className="bg-green-600 hover:bg-green-700"
              data-testid="send-whatsapp-btn"
            >
              <Send className="w-4 h-4 me-2" />
              {sending ? (isArabic ? 'جاري الإرسال...' : 'Sending...') : (isArabic ? 'إرسال عبر الواتساب' : 'Send via WhatsApp')}
            </Button>
          )}
        </div>
      </div>
      
      {/* Printable Content */}
      <div ref={printRef} dir={bilingualPrint ? 'rtl' : (printLanguage === 'ar' ? 'rtl' : 'ltr')}>

      {/* Print Header - Show only in print */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-3xl font-bold text-primary">KE Gents Saloon</h1>
        <h2 className="text-xl mt-2">{getBilingualText('weeklyReport')}</h2>
        <p className="text-muted-foreground mt-1">
          {report.week_start} - {report.week_end}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
        <div className="bg-card border border-white/5 rounded-xl p-6 print:border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-lg print:bg-green-100">
              <DirhamIconColored className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{getBilingualText('revenue')}</p>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={report.total_revenue} /></p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6 print:border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-500/20 rounded-lg print:bg-red-100">
              <TrendingDown className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{getBilingualText('expenses')}</p>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={report.total_expenses} /></p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6 print:border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-lg print:bg-yellow-100">
              <TrendingUp className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{getBilingualText('netProfit')}</p>
              <p className={`text-2xl font-bold ${report.net_profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <CurrencyDisplay amount={report.net_profit} className={report.net_profit >= 0 ? 'text-green-500' : 'text-red-500'} />
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6 print:border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg print:bg-blue-100">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{getBilingualText('clients')}</p>
              <p className="text-2xl font-bold">{report.total_clients}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Threshold Notice */}
      <div className={`p-4 rounded-xl border ${
        report.total_clients >= 40 
          ? 'bg-green-500/10 border-green-500/30' 
          : 'bg-yellow-500/10 border-yellow-500/30'
      } print:hidden`}>
        <div className="flex items-center gap-3">
          {report.total_clients >= 40 ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-yellow-500" />
          )}
          <div>
            <p className="font-medium">
              {report.total_clients >= 40 
                ? (isArabic ? 'التقرير مؤهل للإرسال التلقائي' : 'Report eligible for auto-send')
                : (isArabic ? 'التقرير غير مؤهل للإرسال التلقائي' : 'Report not eligible for auto-send')}
            </p>
            <p className="text-sm text-muted-foreground">
              {isArabic ? 'عدد العملاء' : 'Clients count'}: {report.total_clients} / {isArabic ? 'الحد الأدنى المطلوب' : 'Minimum required'}: 40 {isArabic ? 'عميل' : 'clients'}
            </p>
          </div>
        </div>
      </div>

      {/* Barbers Performance */}
      <div className="bg-card border border-white/5 rounded-xl p-6 print:border-gray-200" data-testid="barbers-performance">
        <h3 className="text-lg font-bold mb-4">{getBilingualText('barbersPerformance')}</h3>
        
        {report.barber_stats && report.barber_stats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/30 print:bg-gray-100">
                <tr>
                  <th className="p-3 text-start">{getBilingualText('barber')}</th>
                  <th className="p-3 text-start">{getBilingualText('revenue')}</th>
                  <th className="p-3 text-start">{getBilingualText('clients')}</th>
                  <th className="p-3 text-start">{getBilingualText('avgPerClient')}</th>
                </tr>
              </thead>
              <tbody>
                {report.barber_stats.map((barber, index) => (
                  <tr 
                    key={barber.barber_id || index} 
                    className="border-t border-white/5 print:border-gray-200"
                  >
                    <td className="p-3 font-medium">{barber.name}</td>
                    <td className="p-3"><CurrencyDisplay amount={barber.revenue} /></td>
                    <td className="p-3">{barber.clients}</td>
                    <td className="p-3">
                      <CurrencyDisplay amount={barber.clients > 0 ? barber.revenue / barber.clients : 0} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-secondary/30 print:bg-gray-100 font-bold">
                <tr>
                  <td className="p-3">{getBilingualText('total')}</td>
                  <td className="p-3"><CurrencyDisplay amount={report.total_revenue} /></td>
                  <td className="p-3">{report.total_clients}</td>
                  <td className="p-3">
                    <CurrencyDisplay amount={report.total_clients > 0 ? report.total_revenue / report.total_clients : 0} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-4">{getBilingualText('noData')}</p>
        )}
      </div>

      {/* Print Footer */}
      <div className="hidden print:block text-center mt-8 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          KE Gents Saloon - {getBilingualText('generatedOn')} {new Date().toLocaleDateString(printLanguage === 'ar' ? 'ar-AE' : 'en-US')}
        </p>
      </div>
      
      </div>{/* End Printable Content */}

      {/* Report Logs (Admin Only) */}
      {isAdmin() && logs.length > 0 && (
        <div className="bg-card border border-white/5 rounded-xl p-6 print:hidden" data-testid="report-logs">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {isArabic ? 'سجل التقارير المرسلة' : 'Sent Reports Log'}
          </h3>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {logs.map((log) => (
              <div 
                key={log.id} 
                className={`p-3 rounded-lg border ${
                  log.sent 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {log.sent ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="font-medium">
                      {log.week_start} - {log.week_end}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {new Date(log.created_at).toLocaleDateString(isArabic ? 'ar-AE' : 'en-US')}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {log.sent 
                    ? `${isArabic ? 'تم الإرسال إلى' : 'Sent to'} ${log.send_to} - ${isArabic ? 'العملاء' : 'Clients'}: ${log.total_clients}`
                    : log.reason}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyReport;
